import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import DotSeparator from '@sharedCR/common/DotSeparator';
import { DataFieldCDS } from '@sharedCR/coreDesignSystem/form';
import { ArrowCheckIcon, DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import { nanoid } from 'nanoid';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconDirection } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT_FOR_TREE_ITEM } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { CicReasonStatusEnum } from 'constants/status';
import { formatDate } from 'utils/date';
import { getPersonFullName } from 'utils/loanApplication';

import {
  closeConfirmationDialogAction,
  closeDialogAction,
  openConfirmationDialogAction,
  openDialogAction,
  updateDialogOptionsAction,
} from 'store/global/actions';

import { LoanFeesTestIds } from '../../../../../LoanFees.testIds';

import { SHOW_DELETE_BUTTON_STATUSES } from './CicFeeReasonEditItem.constants';
import {
  accordionDetailsStyles,
  accordionSummaryStyles,
  amountInputContainerStyles,
  getAccordionStyles,
  reasonMetadataStyles,
  reasonTitleWrapperStyles,
  summaryContentStyles,
  triggerFieldsStyles,
} from './CicFeeReasonEditItem.styles';
import CicReasonStatus from './CicReasonStatus';
import DeleteReasonForm from './DeleteReasonForm';
import NoteSection from './NoteSection';

const CicFeeReasonEditItem = ({
  reason,
  defaultExpanded = true,
  openCICModal,
  formContext,
  isReadOnly = false,
}) => {
  const dispatch = useDispatch();

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const formRef = useRef(null);

  const deleteModalId = useMemo(() => nanoid(), []);

  const {
    loanCicFeeChangeReasonId,
    loanCicReasonId,
    cicReasonName,
    addedBy,
    addedDate,
    totalFeeAmountDiff,
    triggerFields,
  } = reason;

  const reasonId = loanCicFeeChangeReasonId ?? loanCicReasonId;
  const hasFeeAmount = !!totalFeeAmountDiff;

  const { deletionReason: formDeletionReason, reasonStatus: formReasonStatus } =
    formContext.form[reasonId] ?? {};

  const isDeleted = formReasonStatus === CicReasonStatusEnum.DELETED;

  const { oldAmount, newAmount } = totalFeeAmountDiff || {};

  const isShownDeleteButton = !isReadOnly && SHOW_DELETE_BUTTON_STATUSES.includes(formReasonStatus);

  const onChangeAccordion = useCallback((_, expanded) => {
    setIsExpanded(expanded);
  }, []);

  const onCancelDeletion = useCallback(() => {
    openCICModal();
  }, [openCICModal]);

  const onAcceptDeletion = useCallback(() => {
    const statusPath = `${reasonId}.reasonStatus`;

    formContext.setInForm(statusPath, CicReasonStatusEnum.DELETED);

    dispatch(closeConfirmationDialogAction());

    openCICModal();
  }, [dispatch, formContext, reasonId, openCICModal]);

  const onContinueToConfirm = useCallback(async () => {
    dispatch(closeDialogAction());

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Delete CIC Reason',
        description: 'Please confirm that you want to delete this CIC Reason.',
        onAcceptButtonName: 'Delete',
        onCancelButtonName: 'Cancel',
        onAccept: onAcceptDeletion,
        onCancel: onCancelDeletion,
      }),
    );
  }, [dispatch, onAcceptDeletion, onCancelDeletion]);

  const onDelete = useCallback(
    e => {
      e.stopPropagation();

      dispatch(
        openDialogAction({
          id: deleteModalId,
          header: 'Delete CIC Reason',
          isRestyled: true,
          size: ModalSize.SMALL,
          withButtons: true,
          onAcceptButtonName: 'Continue',
          onCancelButtonName: 'Cancel',
          isAcceptButtonDisabled: true,
          onAcceptClick: onContinueToConfirm,
          body: () => (
            <DeleteReasonForm formContext={formContext} formRef={formRef} reason={reason} />
          ),
        }),
      );
    },
    [deleteModalId, dispatch, formContext, onContinueToConfirm, reason],
  );

  useEffect(() => {
    dispatch(
      updateDialogOptionsAction(deleteModalId, {
        isAcceptButtonDisabled: !formDeletionReason,
        body: () => (
          <DeleteReasonForm formContext={formContext} formRef={formRef} reason={reason} />
        ),
      }),
    );
  }, [deleteModalId, dispatch, formContext, openCICModal, reason, formDeletionReason]);

  return (
    <Accordion
      data-testid={LoanFeesTestIds.LOAN_CIC_FEES_FORM_EDIT_CIC_ITEM}
      expanded={isExpanded}
      sx={getAccordionStyles({ isDeleted })}
      onChange={onChangeAccordion}
    >
      <AccordionSummary
        expandIcon={<ArrowCheckIcon direction={IconDirection.DOWN} />}
        sx={accordionSummaryStyles}
      >
        <Box sx={summaryContentStyles}>
          <Box sx={reasonTitleWrapperStyles}>
            <Typography
              color={isDeleted ? 'text.secondary' : 'text.primary'}
              component="p"
              variant="medium20"
            >
              {cicReasonName}
            </Typography>
            <CicReasonStatus status={formReasonStatus} />
          </Box>

          {isShownDeleteButton && (
            <IconButton
              data-testid={LoanFeesTestIds.getCicReasonDeleteButtonId(reasonId)}
              size="small"
              onClick={onDelete}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={accordionDetailsStyles}>
        <Divider />
        <Typography color="text.secondary" sx={reasonMetadataStyles} variant="regular12">
          <span>Added:</span>
          {formatDate(addedDate, COMMON_DATETIME_FORMAT_FOR_TREE_ITEM)}
          <DotSeparator />
          <span>By:</span>
          {getPersonFullName(addedBy)}
        </Typography>

        {!!triggerFields?.length && (
          <>
            <Divider />
            <Typography color="text.secondary" sx={triggerFieldsStyles} variant="regular12">
              <span>Changed:</span>
              {triggerFields.join(', ')}
            </Typography>
          </>
        )}

        <Divider />

        {hasFeeAmount && (
          <>
            <Box sx={amountInputContainerStyles}>
              <DataFieldCDS
                disabled
                fullWidth
                label="New Fee Amount"
                name={`newAmount-${reasonId}`}
                size="small"
                type={FieldInputType.CURRENCY}
                value={newAmount}
              />
              <DataFieldCDS
                disabled
                fullWidth
                label="Old Fee Amount"
                name={`oldAmount-${reasonId}`}
                size="small"
                type={FieldInputType.CURRENCY}
                value={oldAmount}
              />
            </Box>

            <Divider />
          </>
        )}

        <NoteSection formContext={formContext} isReadOnly={isReadOnly} reason={reason} />
      </AccordionDetails>
    </Accordion>
  );
};

CicFeeReasonEditItem.propTypes = {
  formContext: PropTypes.object.isRequired,
  reason: PropTypes.shape({
    cicReasonName: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    addedBy: PropTypes.shape({
      userId: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }).isRequired,
    addedDate: PropTypes.string.isRequired,
    loanCicFeeChangeReasonId: PropTypes.string,
    loanCicReasonId: PropTypes.string,
    cicReasonId: PropTypes.string,
    modifiedBy: PropTypes.shape({
      userId: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
    modifiedDate: PropTypes.string,
    deletionReason: PropTypes.string,
    triggerFields: PropTypes.arrayOf(PropTypes.string),
    totalFeeAmountDiff: PropTypes.shape({
      oldAmount: PropTypes.number,
      newAmount: PropTypes.number,
    }),
    note: PropTypes.string,
  }).isRequired,
  openCICModal: PropTypes.func.isRequired,
  defaultExpanded: PropTypes.bool,
  isReadOnly: PropTypes.bool,
};

export default CicFeeReasonEditItem;
